import React, { useMemo, useState, useEffect, useCallback } from "react";
import "../Complexes/ResidentialComplexList.css";
import IncidentBadge from "../Utils/IncidentBadge";

// те же иконки, что и в карточке комплекса
import icLeak from "../../assets/icons/incidents/ic-inc-leak.svg";
import icBatteryLow from "../../assets/icons/incidents/ic-inc-battery-low.svg";
import icTamper from "../../assets/icons/incidents/ic-inc-tamper.svg";
import icInspection from "../../assets/icons/incidents/ic-inc-inspection.svg";
import icBreak from "../../assets/icons/incidents/ic-inc-break.svg";
import icScreenBlock from "../../assets/icons/incidents/ic-inc-screen-block.svg";
import icNoReadings from "../../assets/icons/incidents/ic-inc-no-readings.svg";
import { useUnit } from "effector-react";
import { $deletingBuildingIds, deleteBuildingFx } from "../../store/buildings";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Switch,
} from "@mui/material";
import { $canDeleteComplex, $canEditComplex } from "../../store";
import EditBuildingDialog from "./EditBuildingDialog";
import { buildingsApi } from "../../api/buildings";

const ICONS = {
  leak: icLeak,
  lowBattery: icBatteryLow,
  tamper: icTamper,
  inspection: icInspection,
  break: icBreak,
  screenBlock: icScreenBlock,
  noReadings: icNoReadings,
};

function stopAll(e) {
  e.stopPropagation?.();
}

export default function BuildingRow({
  id,
  name,
  address,
  counters = { cold: 0, hot: 0, heat: 0, heatAllocators: 0, electricity: 0, all: 0 },
  incidents = {},
  manualSubmissionEnabled = false,
  onClick,
  isNew = false,
  onEdited,
  settlement = false,
}) {
  // на всякий случай приводим к нулю
  const data = useMemo(
    () => ({
      leak: incidents.leak ?? 0,
      lowBattery: incidents.lowBattery ?? 0,
      tamper: incidents.tamper ?? 0,
      inspection: incidents.inspection ?? 0,
      break: incidents.break ?? 0,
      screenBlock: incidents.screenBlock ?? 0,
      noReadings: incidents.noReadings ?? 0,
    }),
    [incidents]
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiveAcknowledged, setArchiveAcknowledged] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [deletePending, setDeletePending] = useState(false);
  const [canDelete, canEdit, deletingIds, deleteBuilding] = useUnit([
    $canDeleteComplex,
    $canEditComplex,
    $deletingBuildingIds,
    deleteBuildingFx,
  ]);
  const deleting = deletingIds.has(String(id));
  const [editOpen, setEditOpen] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [manualEnabled, setManualEnabled] = useState(!!manualSubmissionEnabled);

  useEffect(() => {
    setManualEnabled(!!manualSubmissionEnabled);
  }, [manualSubmissionEnabled]);

  const handleDelete = useCallback(
    async () => {
      if (!canDelete) return;
      setDeletePending(true);
      setArchiveError("");
      try {
        await deleteBuilding({ id: String(id), force: false });
        return true;
      } catch (err) {
        console.error("delete building failed", err);
        const detail = err?.response?.data?.detail;
        setArchiveError(
          typeof detail === "string"
            ? detail
            : detail?.message || "Не удалось переместить объект в корзину."
        );
        return false;
      } finally {
        setDeletePending(false);
      }
    },
    [canDelete, deleteBuilding, id]
  );
  const handleCardClick = (event) => {
    if (!onClick) return;
    if (editOpen || confirmOpen) {
      event?.preventDefault?.();
      return;
    }
    onClick();
  };
  const handleKey = (e) => {
    if (!onClick || editOpen || confirmOpen) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`rc-building-row ${settlement ? "rc-building-row--settlement" : ""} ${onClick ? "is-clickable" : ""} ${
        isNew ? "is-new" : ""
      }`}
      onClick={handleCardClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKey}
    >
      {canEdit && (
        <Tooltip
          title={manualEnabled ? "Ручная передача: включена" : "Ручная передача: выключена"}
          style={{ zIndex: 2 }}
        >
          <span
            style={{
              position: "absolute",
              top: 8,
              right: canDelete ? 84 : 44,
              zIndex: 2,
              display: "inline-flex",
              alignItems: "center",
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onTouchStart={stopAll}
            onPointerDown={stopAll}
            data-interactive="true"
          >
            <Switch
              size="small"
              checked={manualEnabled}
              disabled={toggleLoading}
              onChange={async (e) => {
                stopAll(e);
                const nextValue = !manualEnabled;
                setManualEnabled(nextValue);
                setToggleLoading(true);
                try {
                  await buildingsApi.updateBuilding(String(id), {
                    manual_submission_enabled: nextValue,
                  });
                  await onEdited?.(id);
                } catch (err) {
                  setManualEnabled(!nextValue);
                } finally {
                  setToggleLoading(false);
                }
              }}
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              onPointerDown={stopAll}
              inputProps={{ "aria-label": "Ручная передача показаний" }}
            />
          </span>
        </Tooltip>
      )}
      {canEdit && (
        <Tooltip title={settlement ? "Редактировать дом" : "Редактировать здание"} style={{ zIndex: 2 }}>
          <span
            style={{
              position: "absolute",
              top: 8,
              right: canDelete ? 44 : 8,
              zIndex: 2,
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onTouchStart={stopAll}
            onPointerDown={stopAll}
            data-interactive="true"
          >
            <IconButton
              size="small"
              color="primary"
              aria-label={settlement ? "Редактировать дом" : "Редактировать здание"}
              onClick={(e) => {
                stopAll(e);
                setEditOpen(true);
              }}
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              onPointerDown={stopAll}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") stopAll(e);
              }}
              sx={{
                bgcolor: (t) =>
                  t.palette.mode === "light"
                    ? "rgba(0,0,0,0.04)"
                    : "rgba(255,255,255,0.06)",
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "light"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.12)",
                },
              }}
              data-interactive="true"
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title={settlement ? "Переместить дом в архив" : "Переместить здание в архив"} style={{zIndex: 2}}>
          <span
            style={{ position: "absolute", top: 8, right: 8, zIndex: 2}}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            data-interactive="true"
          >
            <IconButton
              size="small"
              color="error"
              disabled={deleting || deletePending}
              aria-label={settlement ? "Переместить дом в архив" : "Переместить здание в архив"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setArchiveAcknowledged(false);
                setArchiveError("");
                setConfirmOpen(true);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
              sx={{
                bgcolor: (t) =>
                  t.palette.mode === "light"
                    ? "rgba(0,0,0,0.04)"
                    : "rgba(255,255,255,0.06)",
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "light"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.12)",
                },
              }}
              data-interactive="true"
            >
              {deleting || deletePending ? (
                <CircularProgress size={18} />
              ) : (
                <DeleteOutlineRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {settlement ? (
        <div className="rc-settlement-house">
          <div className="rc-settlement-house__icon" aria-hidden="true">
            <HomeRoundedIcon />
          </div>
          <div className="rc-settlement-house__content">
            <div className="rc-settlement-house__name" title={name || address || ""}>
              {name || "Дом"}
            </div>
            <div className="rc-settlement-house__address" title={address || ""}>
              {address || "Адрес не указан"}
            </div>
            <div className="rc-settlement-house__meters">
              <span className="rc-settlement-house__total">
                Приборов <strong>{counters.all ?? 0}</strong>
              </span>
              {(counters.cold ?? 0) > 0 && <span>ХВС {counters.cold}</span>}
              {(counters.hot ?? 0) > 0 && <span>ГВС {counters.hot}</span>}
              {(counters.heat ?? 0) > 0 && <span>Тепло {counters.heat}</span>}
              {(counters.electricity ?? 0) > 0 && <span>Электро {counters.electricity}</span>}
              {(counters.heatAllocators ?? 0) > 0 && <span>УРТ {counters.heatAllocators}</span>}
            </div>
          </div>
          <div className="rc-settlement-house__open" aria-hidden="true">
            <ArrowForwardRoundedIcon />
          </div>
        </div>
      ) : (
      <>
      {/* левая: адрес */}
      <div className="rc-brow-col rc-brow-col--addr">
        <div className="rc-brow-title">Название</div>
        <div className="rc-brow-name" title={name || ""}>
          {name || "—"}
        </div>
        <div className="rc-brow-title">Адрес</div>
        <div className="rc-brow-addr" title={address || ""}>
          {address || "—"}
        </div>
      </div>

      {/* середина: счётчики */}
      <div className="rc-brow-col rc-brow-col--meters">
        <div className="rc-brow-title">Количество счётчиков</div>
        <div className="rc-meters rc-meters--stack rc-meters--tight">
          <div className="rc-meter">
            <div className="rc-meter__type">ХВС</div>
            <div className="rc-meter__value">{counters.cold ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">ГВС</div>
            <div className="rc-meter__value">{counters.hot ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Отопление</div>
            <div className="rc-meter__value">{counters.heat ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">УРТ</div>
            <div className="rc-meter__value">{counters.heatAllocators ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Электросчетчики</div>
            <div className="rc-meter__value">{counters.electricity ?? 0}</div>
          </div>
          <div className="rc-meter">
            <div className="rc-meter__type">Всего</div>
            <div className="rc-meter__value">{counters.all ?? 0}</div>
          </div>
        </div>
      </div>

      {/* правая: инциденты — как в карточке комплекса */}
      <div className="rc-brow-col rc-brow-col--inc">
        <div className="rc-brow-title">Инциденты</div>

        <div className="rc-incidents rc-incidents--two rc-incidents--compact">
          {/* левая подколонка */}
          <div className="rc-inc-col">
            {[
              { key: "leak", label: "Протечки", alertIf: (n) => n > 0 },
              { key: "lowBattery", label: "Низкий заряд" },
              { key: "tamper", label: "Воздействие магнитом" },
              { key: "inspection", label: "Поверка" },
              { key: "break", label: "Прорыв" },
            ].map(({ key, label, alertIf }) => (
              <div key={key} className="rc-inc-row">
                <span className="rc-inc-label">{label}</span>
                <IncidentBadge
                  icon={ICONS[key]}
                  count={data[key]}
                  alert={alertIf ? alertIf(data[key]) : false}
                  title={label}
                />
              </div>
            ))}
          </div>

          {/* правая подколонка */}
          <div className="rc-inc-col rc-inc-col_right">
            {[
              { key: "screenBlock", label: "Блок. экрана" },
              {
                key: "noReadings",
                label: "Отсутствие показаний",
                alertIf: (n) => n > 0,
              },
            ].map(({ key, label, alertIf }) => (
              <div key={key} className="rc-inc-row">
                <span className="rc-inc-label">{label}</span>
                <IncidentBadge
                  icon={ICONS[key]}
                  count={data[key]}
                  alert={alertIf ? alertIf(data[key]) : false}
                  title={label}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      </>
      )}
      <EditBuildingDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        building={{ id, name, address }}
        settlement={settlement}
        onSaved={async () => {
          setEditOpen(false);
          await onEdited?.(id);
        }}
      />
      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!deletePending) setConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
        onClick={stopAll}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800 }}>
          Переместить в корзину?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              {settlement ? "Дом" : "Здание"} «{name || id}» исчезнет из рабочих списков.
            </Typography>
            {(counters.all ?? 0) > 0 && (
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                <Typography fontWeight={800} color="text.primary">
                  Приборов: {new Intl.NumberFormat("ru-RU").format(counters.all)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Сбор показаний с этих приборов остановится сразу.
                </Typography>
              </Alert>
            )}
            {archiveError && <Alert severity="error">{archiveError}</Alert>}
            <Stack spacing={0.75}>
              <Typography variant="body2">Объект хранится в корзине 7 дней.</Typography>
              <Typography variant="body2">До окончания срока его можно восстановить.</Typography>
              <Typography variant="body2">Показания и настройки при этом сохраняются.</Typography>
            </Stack>
            {(counters.all ?? 0) > 0 && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={archiveAcknowledged}
                    onChange={(event) => setArchiveAcknowledged(event.target.checked)}
                  />
                )}
                label="Я понимаю, что сбор показаний будет остановлен"
                sx={{ alignItems: "flex-start", m: 0 }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deletePending}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletePending || ((counters.all ?? 0) > 0 && !archiveAcknowledged)}
            onClick={async () => {
              if (await handleDelete()) setConfirmOpen(false);
            }}
          >
            {deletePending ? <CircularProgress size={20} color="inherit" /> : "В корзину"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
